import React, { memo, useCallback, useState, useEffect, ReactNode } from 'react';
import Tag from 'antd/es/tag';
import cx from 'clsx';

import AutoTooltip from '../AutoTooltip';
import './index.scss';

const { CheckableTag: AntdCheckableTag } = Tag;

export type CheckableTagItem = {
  label: ReactNode;
  value: any;
};

type CheckableTagProps = {
  required?: boolean;
  data?: CheckableTagItem[];
  selected?: CheckableTagItem | CheckableTagItem[];
  multiple?: boolean;
  onChange?: (selected?: CheckableTagItem | CheckableTagItem[]) => void;
  className?: string;
  tagClassName?: string;
  tagSelectedClassName?: string;
  auto?: boolean;
};

export const CheckableTag = memo(
  ({
    data = [],
    selected,
    required = true,
    multiple = false,
    className,
    tagClassName,
    tagSelectedClassName,
    onChange,
    auto = true,
  }: CheckableTagProps) => {
    if (!multiple && Array.isArray(selected)) {
      throw new Error('multiple checkable tag should only has one selected!');
    }

    const [_selected, setSelected] = useState(
      selected ? (Array.isArray(selected) ? [...selected] : [selected]) : [],
    );

    useEffect(() => {
      setSelected(selected ? (Array.isArray(selected) ? [...selected] : [selected]) : []);
    }, [selected]);

    const handleChange = useCallback(
      (i, checked) => {
        const item = data[i];

        const nextSelectedTags = checked
          ? multiple
            ? [..._selected, item]
            : [item]
          : _selected.filter(t => t.value !== item.value);

        if (required && !nextSelectedTags.length) {
          return;
        }

        setSelected(nextSelectedTags);
        onChange?.(multiple ? nextSelectedTags : nextSelectedTags[0]);
      },
      [data, multiple, _selected, onChange, required],
    );

    return (
      <div className={cx('db-checkable-tag', className)}>
        {data.map((tag, i) => {
          const checked = _selected.some(x => x.value === tag.value);

          return (
            <AntdCheckableTag
              className={cx('db-checkable-tag-item', tagClassName, checked && tagSelectedClassName)}
              key={+i}
              checked={checked}
              onChange={checked => handleChange(i, checked)}>
              <AutoTooltip title={tag.label} auto={auto}>
                {tag.label}
              </AutoTooltip>
            </AntdCheckableTag>
          );
        })}
      </div>
    );
  },
);

export default CheckableTag;
